import { useAppDispatch } from '../store/hooks';
import { removeCard } from '../store/cardsSlice';
import type { Card as CardType } from '../store/cardsSlice';
import ResizeHandles from './ResizeHandles';
import MarketWatch from './Cards/MarketWatch';
import Charts from './Cards/Charts';
import Orders from './Cards/Orders';
import ConnectionWarning from './Cards/ConnectionWarning';
import Positions from './Cards/Positions';
import Chat from './Cards/Chat';
import Funds from './Cards/Funds';

interface CardProps {
    card: CardType;
    blinkingCardId: string | null;
    onMouseDown: (e: React.MouseEvent, id: string) => void;
    onResizeMouseDown: (e: React.MouseEvent, id: string, edge: string) => void;
}

const Card = ({ card, blinkingCardId, onMouseDown, onResizeMouseDown }: CardProps) => {
    const dispatch = useAppDispatch();

    const closeCard = (id: string) => {
        dispatch(removeCard(id));
    };

    // Render content based on content type
    const renderContent = () => {
        console.log("Rendering content for card:", card);
        switch (card.content) {
            case 'market':
                return <MarketWatch />;
            case 'portfolio':
                return <Positions />;
            case 'trades':
                return <Orders />;
            case 'funds':
                return <Funds />;
            case 'analytics':
                return 'Trading analytics and insights';
            case 'news':
                return 'Latest market news and updates';
            case 'charts':
                return <Charts />;
            case 'chat':
                return <Chat />;
            case 'connection-warning':
                return <ConnectionWarning />;
            default:
                return card.content;
        }
    };

    return (
        <div
            onMouseDown={(e) => onMouseDown(e, card.id)}
            className="card-container"
            style={{
                position: 'absolute',
                top: card.position.y,
                left: card.position.x,
                width: `min(${card.size.width}px, calc(100vw - 32px))`,
                height: card.size.height,
                maxWidth: 'calc(100vw - 32px)',
                backgroundColor: 'var(--color-surface)',
                border: '1px solid var(--color-border-light)',
                boxShadow: 'var(--shadow-xl)',
                zIndex: card.zIndex,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'visible',
                cursor: 'move',
                animation: blinkingCardId === card.id ? 'blink 0.5s ease-in-out 4' : 'none'
            }}
        >
            {/* Card Content Wrapper */}
            <div style={{
                backgroundColor: 'var(--color-surface)',
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
                height: '100%',
                position: 'relative',
                borderRadius: 'inherit'
            }}>
                {/* Card Header - Shown only on hover */}
                <div
                    className="card-controls card-header"
                    style={{
                        position: 'absolute',
                        top: 'auto',
                        bottom: '100%',
                        left: 0,
                        right: 0,
                        padding: 'var(--spacing-xs) var(--spacing-sm)',
                        border: '1px solid var(--color-border-light)',
                        borderBottom: '1px solid var(--color-border-light)',
                        backgroundColor: 'var(--color-gray-50)',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        cursor: 'move',
                        opacity: 0,
                        transform: 'translateY(4px)',
                        transition: 'opacity 200ms ease-in-out, transform 200ms ease-in-out',
                        zIndex: 10,
                        pointerEvents: 'none',
                        borderTopLeftRadius: 'var(--radius-md)',
                        borderTopRightRadius: 'var(--radius-md)',
                        boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)'
                    }}
                >
                    <h3 style={{
                        fontSize: '0.875rem',
                        fontWeight: '600',
                        color: 'var(--color-text-primary)',
                        margin: 0
                    }}>
                        {card.title}
                    </h3>

                    <div style={{ display: 'flex', gap: '4px' }}>
                        {/* Close Button */}
                        <button
                            onClick={(e) => {
                                e.stopPropagation();
                                closeCard(card.id);
                            }}
                            style={{
                                width: '24px',
                                height: '24px',
                                borderRadius: 'var(--radius-md)',
                                border: '1px solid var(--color-border-light)',
                                backgroundColor: 'var(--color-surface)',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 200ms ease-in-out',
                                color: 'var(--color-text-secondary)'
                            }}
                            onMouseEnter={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-danger-50)';
                                e.currentTarget.style.borderColor = 'var(--color-danger-200)';
                                e.currentTarget.style.color = 'var(--color-danger-600)';
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.backgroundColor = 'var(--color-surface)';
                                e.currentTarget.style.borderColor = 'var(--color-border-light)';
                                e.currentTarget.style.color = 'var(--color-text-secondary)';
                            }}
                        >
                            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                                <line x1="18" y1="6" x2="6" y2="18" strokeWidth="2" strokeLinecap="round" />
                                <line x1="6" y1="6" x2="18" y2="18" strokeWidth="2" strokeLinecap="round" />
                            </svg>
                        </button>
                    </div>
                </div>

                {/* Card Content */}
                <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    overflow: 'auto',
                    color: 'var(--color-text-primary)'
                }}>
                    {renderContent()}
                </div>

                {/* Resize Handles */}
                <ResizeHandles cardId={card.id} onResizeMouseDown={onResizeMouseDown} />
            </div>

            <style>{`
                .card-container:hover .card-header {
                    opacity: 1 !important;
                    transform: translateY(0) !important;
                    pointer-events: auto !important;
                }
            `}</style>
        </div>
    );
};

export default Card;